import React from "react";
import { SkeletonBase, SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import {
  CHROME_PADDING,
  CHROME_SEAM_ABOVE_NAV,
  CHROME_STACK,
  CHROME_SUBLINE_STRIP,
  CHROME_TRIGGER,
  COLLAPSED_SQUARE,
  NAV_PADDING,
  ROW_GAP,
  ROW_PADDING,
  ROW_STACK,
  SECTION_HEADER_PADDING,
  SECTION_STACK,
} from "../sideMenuDensity";
import { shouldShowTenantCard } from "../sideMenuVisibility";

/**
 * Placeholder rail shown while the user profile is still resolving.
 *
 * Geometry is borrowed from the same density constants the real chrome and nav
 * use, so the swap to real content does not move anything: the rail keeps its
 * width, and rows keep their rhythm. Row counts below are a plausible menu, not
 * a promise — the real tree replaces them wholesale.
 */

/**
 * Rows per placeholder section, mirroring a typical product's nav shape.
 *
 * The last entry is the Account section (CTX-512), and unlike the others it is
 * not a guess: every product renders it, so reserving its height is right for
 * every tenant that sees all three rows.
 */
const SECTION_ROW_COUNTS = [1, 4, 2, 3];

const SkeletonRow: React.FC<{ showLabels: boolean; labelWidth: string }> = ({
  showLabels,
  labelWidth,
}) => (
  <div
    className={`flex items-center ${ROW_GAP} ${ROW_PADDING} ${
      showLabels ? "" : "justify-center"
    }`}
  >
    {/* Matches ROW_ICON's 13px box so the icon column lines up exactly.
        `rounded`, not `rounded-sm`: this repo overrides Tailwind's radius
        scale (`rounded-sm` is 8px here, not 2px), which on a 13px box
        exceeds half the side and renders a circle. */}
    <SkeletonBase decorative w={13} h={13} roundedClass="rounded" />
    {showLabels && <SkeletonLine decorative w={labelWidth} h={10} />}
  </div>
);

/** Varied but fixed label widths — random widths would shimmer between renders. */
const LABEL_WIDTHS = ["70%", "55%", "80%", "45%", "65%"];

/**
 * Placeholder for the tenant card, shaped like the real bordered box rather than
 * given a flat height, so it tracks `CHROME_TRIGGER` / `CHROME_SUBLINE_STRIP`
 * instead of restating them as a number that would silently go stale.
 */
const SkeletonTenantCard: React.FC = () => (
  <div className="overflow-hidden rounded-lg border border-semantic-border-secondary">
    <div className={`flex ${CHROME_TRIGGER} items-center px-2`}>
      <SkeletonLine decorative w="75%" h={10} />
    </div>
    <div
      className={`flex ${CHROME_SUBLINE_STRIP} items-center border-t border-semantic-border-secondary px-2`}
    >
      <SkeletonLine decorative w="50%" h={8} />
    </div>
  </div>
);

export type SideMenuSkeletonProps = {
  isExpanded: boolean;
  isMobileView: boolean;
  /** Empty while the profile is in flight — see the tenant-card slot below. */
  tenantType: string;
};

const SideMenuSkeleton: React.FC<SideMenuSkeletonProps> = ({
  isExpanded,
  isMobileView,
  tenantType,
}) => {
  // Same rule as SideMenuNavByProduct: the collapsed rail is icon-only.
  const showLabels = isExpanded || isMobileView;

  /**
   * Whether to reserve the tenant card, decided by the same predicate the real
   * card calls so the two cannot disagree.
   *
   * The tenant type is genuinely unknown here — it arrives with the profile
   * this skeleton is waiting on, and the persisted `selection` slice does not
   * carry it. The predicate's own answer for an unresolved type is "show",
   * which is also the majority outcome: every tenant except CUSTOMER keeps the
   * card. Reserving it is therefore right far more often than not, and the
   * cost of being wrong is a CUSTOMER rail settling upward once.
   */
  const showTenantCard = shouldShowTenantCard({
    tenantType,
    collapsed: !showLabels,
  });

  return (
    <div
      role="status"
      aria-busy
      aria-label="Loading navigation"
      className="flex flex-1 flex-col overflow-hidden"
    >
      {/* Chrome: account card + collapse toggle, or the collapsed rail's stack. */}
      {showLabels ? (
        <div
          className={`flex flex-col ${CHROME_STACK} ${CHROME_PADDING} ${CHROME_SEAM_ABOVE_NAV}`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className={`flex min-w-0 flex-1 items-center ${ROW_GAP}`}>
              {/* CHROME_LOGO is h-8 w-8 — kept in step by hand, since a
                  Tailwind class cannot be handed to `SkeletonBase`'s pixel
                  props. `rounded` for the same reason as the row icon:
                  `rounded-md` is 12px here and rounds the box into a circle. */}
              <SkeletonBase decorative w={32} h={32} roundedClass="rounded" />
              <SkeletonLine decorative w="60%" h={10} />
            </div>
            <SkeletonBase
              decorative
              w={32}
              h={32}
              roundedClass="rounded-md"
              className="shrink-0"
            />
          </div>
          {showTenantCard && <SkeletonTenantCard />}
        </div>
      ) : (
        <div className={`flex flex-col items-center ${CHROME_STACK} ${CHROME_PADDING}`}>
          <SkeletonBase
            decorative
            w={32}
            h={32}
            roundedClass="rounded-lg"
            className={COLLAPSED_SQUARE}
          />
          <SkeletonBase decorative w={32} h={32} roundedClass="rounded-md" />
        </div>
      )}

      {/* Nav: same padding and section rhythm as the real tree. */}
      <div className="flex-1 overflow-hidden">
        <nav className={`${NAV_PADDING} ${SECTION_STACK}`}>
          {SECTION_ROW_COUNTS.map((rowCount, sectionIndex) => (
            <div key={sectionIndex}>
              {/* Section headers only exist when labels are shown. */}
              {showLabels && sectionIndex > 0 && (
                <div className={SECTION_HEADER_PADDING}>
                  <SkeletonLine decorative w="40%" h={8} />
                </div>
              )}
              <div className={ROW_STACK}>
                {Array.from({ length: rowCount }).map((_, rowIndex) => (
                  <SkeletonRow
                    key={rowIndex}
                    showLabels={showLabels}
                    labelWidth={LABEL_WIDTHS[rowIndex % LABEL_WIDTHS.length]}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default SideMenuSkeleton;
